import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Typography,
} from '@mui/material'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { DriverCard, OrdersList, OccasionalDriverModal, ConfirmationModal } from '../../components'
import dayjs from 'dayjs'

function PlanningPageInPage({
  selectedDate,
  setSelectedDate,
  weekDays,
  drivers,
  isPlanificationSentAtLeastOnce,
  isPastDate,
  isTodayOrNextBusinessDay,
  loading,
  error,
  orders,
  isModalOpen,
  handleModalClose,
  handleAddOccasionalDriver,
  handleRefreshPlanning,
  handleSendPlanification,
  handleConfirmSend,
  setIsConfirmationOpen,
  isConfirmationOpen,
  moveOrder,
  moveOrderToList,
  toggleDriverStatus,
  toggleFullScreen,
  onExportExcel,
}) {
  const { t } = useTranslation('planning_page')

  return (
    <Box sx={{ m: 1.5 }}>
      <DndProvider backend={HTML5Backend}>
        <div>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            width="100%"
            mb={2}
          >
            <Box
              display="flex"
              width="95%"
              flexDirection="column"
              justifyContent="center"
              mb={2}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={7}>
                  <Box display="flex" alignItems="center" justifyContent="center" gap={2}>
                    <TextField
                      label={t('choose_week')}
                      type="date"
                      value={selectedDate.format('YYYY-MM-DD')}
                      onChange={(e) => setSelectedDate(dayjs(e.target.value))}
                      InputLabelProps={{ shrink: true }}
                      margin="normal"
                      sx={{
                        minWidth: '200px',
                        backgroundColor: 'white',
                      }}
                    />

                    <Box display="flex" flexDirection="row" gap={1}>
                      {weekDays.map((day) => {
                        const isSelected = dayjs(day).isSame(selectedDate, 'day')
                        return (
                          <Button
                            key={day}
                            onClick={() => setSelectedDate(day)}
                            sx={{
                              backgroundColor: 'white',
                              border: isSelected ? '2px solid blue' : '2px solid gray',
                              color: 'black',
                              '&:hover': {
                                backgroundColor: 'white',
                              },
                            }}
                          >
                            {dayjs(day).format('dddd DD/MM')}
                          </Button>
                        )
                      })}
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={5} container justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={toggleFullScreen}
                    sx={{ minWidth: 40, width: 40, height: 40, p: 0, backgroundColor: 'white', borderColor: '#737373', mr: 1 }}
                  >
                    <img src="/icons/full-screen.svg" alt="" />
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => window.open(window.location.href, '_blank')}
                    sx={{ textTransform: 'uppercase', backgroundColor: 'white', fontWeight: 'bold', boxShadow: '0 4px 4px 0 rgba(0, 0, 0, 0.25)', mr: 1 }}
                  >
                    <img src="/icons/screen-share.svg" alt="" style={{ width: 20, height: 20, marginRight: 4 }} />
                    {t('openPlanningNewTab')}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddOccasionalDriver}
                    style={{ marginRight: '5px' }}
                    disabled={isPastDate}
                  >
                    <span style={{ fontSize: '1.2rem', marginRight: '5px' }}>+</span>
                    {t('occasionalDriver')}
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={onExportExcel}
                    style={{ marginRight: '5px' }}
                  >
                    {t('exportExcel')}
                  </Button>
                  <Button
                    variant={isPlanificationSentAtLeastOnce ? 'outlined' : 'contained'}
                    color={isPlanificationSentAtLeastOnce ? 'success' : 'primary'}
                    disabled={!isTodayOrNextBusinessDay && !isPlanificationSentAtLeastOnce}
                    onClick={
                      isPlanificationSentAtLeastOnce ? undefined : handleSendPlanification
                    }
                    sx={{
                      borderColor: isPlanificationSentAtLeastOnce ? 'green' : undefined,
                      color: isPlanificationSentAtLeastOnce ? 'green' : undefined,
                      '&:hover': {
                        borderColor: isPlanificationSentAtLeastOnce ? 'darkgreen' : undefined,
                        backgroundColor: isPlanificationSentAtLeastOnce
                          ? 'transparent'
                          : undefined,
                      },
                    }}
                  >
                    <span style={{ marginRight: '3px' }}>
                      {isPlanificationSentAtLeastOnce ? (
                        <img
                          src="icons/check.png"
                          alt="Check Icon"
                          style={{
                            width: '20px',
                            height: '20px',
                            filter:
                              'invert(36%) sepia(66%) saturate(473%) hue-rotate(90deg) brightness(90%) contrast(85%)',
                          }}
                        />
                      ) : (
                        <img
                          src="icons/picto-send.svg"
                          alt="Send Icon"
                          style={{ width: '20px', height: '20px' }}
                        />
                      )}
                    </span>
                    {isPlanificationSentAtLeastOnce
                      ? t('planningSent')
                      : t('sendPlanification')}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Box>

          <Card
            style={{
              marginBottom: '20px',
              padding: '10px',
              maxHeight: '650px',
              overflow: 'auto',
            }}
          >
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <CircularProgress
                  size={80}
                  sx={{
                    svg: {
                      width: '100%',
                      height: '100%',
                      viewBox: '0 0 40 40',
                    },
                  }}
                />
              </Box>
            ) : error ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <Typography color="error">{t('errorLoadingData')}</Typography>
              </Box>
            ) : (
              <CardContent>
                <Box display="flex" flexDirection="row" gap={2} flexWrap="wrap">
                  {drivers?.map((driver) => (
                    <DriverCard
                      key={driver.id}
                      driver={driver}
                      transporterColor={driver.transporter.color}
                      moveOrder={moveOrder}
                      toggleDriverStatus={toggleDriverStatus}
                      isDragDisabled={isPastDate}
                    />
                  ))}
                </Box>
              </CardContent>
            )}
          </Card>
          {orders?.orderDeliveries?.edges.length > 0 ? (
            <OrdersList
              deliveryOrders={orders.orderDeliveries.edges.filter(
                (order) => order.node.userId === null
              )}
              moveOrderToList={moveOrderToList}
              isDragDisabled={isPastDate}
            />
          ) : (
            <p>{t('noOrders')}</p>
          )}
        </div>
      </DndProvider>
      <OccasionalDriverModal
        open={isModalOpen}
        onClose={handleModalClose}
        mode={'create'}
        refresh={handleRefreshPlanning}
        selectedDate={selectedDate}
      />
      <ConfirmationModal
        open={isConfirmationOpen}
        onClose={() => setIsConfirmationOpen(false)}
        onConfirm={handleConfirmSend}
        title={t('confirmSendTitle')}
        message={t('confirmSendMessage')}
      />
    </Box>
  )
}

PlanningPageInPage.propTypes = {
  selectedDate: PropTypes.any.isRequired,
  setSelectedDate: PropTypes.func.isRequired,
  weekDays: PropTypes.array.isRequired,
  drivers: PropTypes.array.isRequired,
  isPlanificationSentAtLeastOnce: PropTypes.bool.isRequired,
  isPastDate: PropTypes.bool.isRequired,
  isTodayOrNextBusinessDay: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.any,
  orders: PropTypes.any,
  isModalOpen: PropTypes.bool.isRequired,
  handleModalClose: PropTypes.func.isRequired,
  handleAddOccasionalDriver: PropTypes.func.isRequired,
  handleRefreshPlanning: PropTypes.func.isRequired,
  handleSendPlanification: PropTypes.func.isRequired,
  handleConfirmSend: PropTypes.func.isRequired,
  setIsConfirmationOpen: PropTypes.func.isRequired,
  isConfirmationOpen: PropTypes.bool.isRequired,
  moveOrder: PropTypes.func.isRequired,
  moveOrderToList: PropTypes.func.isRequired,
  toggleDriverStatus: PropTypes.func.isRequired,
  toggleFullScreen: PropTypes.func.isRequired,
  onExportExcel: PropTypes.func.isRequired,
}

export default PlanningPageInPage
